import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';
import { generateUUID } from '@topconagriculture/frontend-utils';
import { FormName } from '@topconagriculture/feed-types';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFarmsListRequest } from '../farms/farms.actions';
import {
  addCloseouts,
  addNote,
  addTransfers,
  createLot,
  deleteLot,
  deleteNote,
  editAdditionalCostsInLot,
  editNote,
  getLotById,
  getLots,
  updateLot,
  updateLotMovements,
} from './lots.actions';
import { Forms } from '../../form/form.selector';
import { appLotsSelector, selectedSelector, settingsSelector } from '../../selectors';
import { MovementInCreation } from '../../application/lots/appLots.types';
import { MovementTypeCode } from '../../../config/constants';
import { appLotsActions, groupActions } from '../../actions';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { Movement } from './lots.types';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { getSelectedLot } from '../../selected/selected.selector';
import { setSelectedLotId } from '../../selected/selected.actions';
import { getLotsFiltered } from './lots.selector';

export function* getLotsSaga(action: ReturnType<typeof getLots.started>) {
  yield put(showLoader(LoadersNames.getLots));
  try {
    const canReadLots = yield call(checkPermissions, { resource: Resource.FEED_LOTS, action: Action.READ });
    if (canReadLots) {
      const result = yield call(api.lots.getLots, false);
      yield put(
        getLots.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getLots));
}

export function* getLotByIdSaga(action: ReturnType<typeof getLotById.started>) {
  yield put(showLoader(LoadersNames.getLotById));
  try {
    const { id, includeCosts } = action.payload;
    const result = yield call(api.lots.getLotById, id, includeCosts);
    yield put(
      getLotById.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getLotById));
}

export function* createLotSaga(action: ReturnType<typeof createLot.started>) {
  yield put(showLoader(LoadersNames.createLot));
  try {
    const formName = action.payload;
    const lotInfo = yield select(getFormValues(Forms.lotsEditorForm));
    const lotCreated = yield call(api.lots.createLot, lotInfo, formName);

    const movements = yield select(appLotsSelector.getArrivals);
    yield all(
      movements.map((x) => call(api.lots.createMovement, lotCreated.id, { ...x, lotId: lotCreated.id }, formName)),
    );

    const result = yield call(api.lots.getLotById, lotCreated.id);

    yield put(
      createLot.done({
        params: action.payload,
        result,
      }),
    );

    const { meta } = action;
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
    }
    yield put(notificationSuccess('lots:create_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createLot));
}

export function* updateLotSaga(action: ReturnType<typeof updateLot.started>) {
  yield put(showLoader(LoadersNames.updateLot));
  try {
    const { id: lotId, formName } = action.payload;
    const lotInfo = yield select(getFormValues(Forms.lotsEditorForm));
    yield call(api.lots.updateLot, lotId, lotInfo, formName);

    const movements = yield select(appLotsSelector.getArrivals);
    yield all(movements.map((x) => call(api.lots.createMovement, lotId, { ...x, lotId }, formName)));

    const result = yield call(api.lots.getLotById, lotId);

    yield put(
      updateLot.done({
        params: action.payload,
        result,
      }),
    );

    const { meta } = action;
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
    }
    yield put(notificationSuccess('lots:edit_lot_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateLot));
}

export function* addTransfersSaga(action: ReturnType<typeof addTransfers.started>) {
  yield put(showLoader(LoadersNames.addTransfers));
  try {
    const formName = action.payload;
    const transfers: MovementInCreation[] = yield select(appLotsSelector.getMovements);
    const timezone = yield select(settingsSelector.getTimeZone);

    const movements = transfers.map((x) => ({
      timestamp: toTimezoneDate(toLocalDate(x.timestamp), timezone),
      fromGroupId: x.fromGroupId,
      headCount: x.headCount,
      lotId: x.lotId,
      toGroupId: x.toGroupId !== '-2' ? x.toGroupId : undefined,
      movementTypeCode: x.toGroupId === '-2' ? MovementTypeCode.Death : MovementTypeCode.Transfer,
      avgWeight: x.avgWeight,
    }));

    const movementsCreated = yield all(
      movements.map((movement) => call(api.lots.createMovement, movement.lotId, movement, formName)),
    );

    const notes = transfers
      .map((x, i) => ({ ...x, movementId: movementsCreated[i].movementId }))
      .filter((x) => x.note)
      .map((x) => ({
        timestamp: x.timestamp,
        lotId: x.lotId,
        movementId: x.movementId,
        text: x.note,
      }));

    yield all(notes.map((note) => call(api.lots.createNote, note.lotId, note, formName)));

    yield put(appLotsActions.resetMovements());

    yield put(getLots.started({}));
    yield put(notificationSuccess('lots:add_transfers_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.addTransfers));
}

export function* addCloseoutsSaga(action: ReturnType<typeof addCloseouts.started>) {
  yield put(showLoader(LoadersNames.addCloseouts));
  try {
    const formName = action.payload;
    const selectedLotId = yield select(selectedSelector.getSelectedLotId);
    const timezone = yield select(settingsSelector.getTimeZone);
    const includedCloseouts: string[] = yield select(appLotsSelector.getIncludedCloseouts);

    let closeouts: MovementInCreation[] = yield select(appLotsSelector.getMovements);
    closeouts = closeouts.filter((x) => includedCloseouts.includes(x.uuid));

    const movements = closeouts.map((x) => ({
      timestamp: toTimezoneDate(x.timestamp, timezone),
      fromGroupId: x.fromGroupId,
      headCount: x.headCount,
      lotId: x.lotId,
      movementTypeCode: MovementTypeCode.Dispatch,
      avgWeight: x.avgWeight,
    }));

    const movementsCreated = yield all(
      movements.map((movement) => call(api.lots.createMovement, movement.lotId, movement, formName)),
    );

    const notes = closeouts
      .map((x, i) => ({ ...x, movementId: movementsCreated[i].movementId }))
      .filter((x) => x.note)
      .map((x) => ({
        timestamp: x.timestamp,
        lotId: x.lotId,
        movementId: x.movementId,
        text: x.note,
      }));

    yield all(notes.map((note) => call(api.lots.createNote, note.lotId, note, formName)));

    yield put(appLotsActions.resetMovements());

    yield put(
      getLotById.started({
        id: selectedLotId,
        includeCosts: false,
      }),
    );
    yield put(groupActions.getGroupList.started({}));
    yield put(notificationSuccess('lots:add_dispatches_success'));

    if (action.meta && typeof action.meta.onSuccess === 'function') {
      action.meta.onSuccess();
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.addCloseouts));
}

export function* addNoteSaga(action: ReturnType<typeof addNote.started>) {
  yield put(showLoader(LoadersNames.addNote));
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.lots.createNote, data.lotId, data, formName);

    yield put(
      addNote.done({
        params: action.payload,
        result,
      }),
    );

    const meta = action.meta;
    if (meta && typeof meta.onSuccess === 'function') {
      meta.onSuccess();
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.addNote));
}

export function* deleteNoteSaga(action: ReturnType<typeof deleteNote.started>) {
  yield put(showLoader(LoadersNames.deleteNote));
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.lots.deleteNote, data.lotId, data.id, formName);

    yield put(
      deleteNote.done({
        params: action.payload,
        result,
      }),
    );

    const meta = action.meta;
    if (meta && typeof meta.onSuccess === 'function') {
      meta.onSuccess();
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.deleteNote));
}

export function* editNoteSaga(action: ReturnType<typeof editNote.started>) {
  yield put(showLoader(LoadersNames.editNote));
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.lots.editNote, data.lotId, data, formName);

    yield put(
      editNote.done({
        params: action.payload,
        result,
      }),
    );

    const meta = action.meta;
    if (meta && typeof meta.onSuccess === 'function') {
      meta.onSuccess();
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editNote));
}

export function* updateLotMovementsSaga(action: ReturnType<typeof updateLotMovements.started>) {
  yield put(showLoader(LoadersNames.updateLotMovements));
  try {
    const { formName, ...lot } = action.payload;
    const id = lot.id;
    const movements = lot.movements.map((x) => {
      const { groups, changed, error, timestamp, toGroupId, fromGroupId, ...movement } = x;
      return {
        ...movement,
        id: movement.id ? movement.id : generateUUID(),
        timestamp: toLocalDate(new Date(timestamp)),
        toGroupId: toGroupId || null,
        fromGroupId: fromGroupId || null,
      } as Movement;
    });

    const result = yield call(api.lots.updateLot, id, { id, movements }, formName);

    yield put(
      updateLot.done({
        params: { id, formName },
        result,
      }),
    );

    const { meta } = action;
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
    }
    yield put(notificationSuccess('lots:edit_lot_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateLotMovements));
}

export function* editAdditionalCostsInLotSaga(action: ReturnType<typeof editAdditionalCostsInLot.started>) {
  yield put(showLoader(LoadersNames.updateLotAdditionalCosts));
  const formValues = yield select(getFormValues(Forms.costTypesEditorForm));
  const selectedLot = yield select(getSelectedLot);
  const timezone = yield select(settingsSelector.getTimeZone);
  try {
    const id = selectedLot.id;

    const additionalCosts = Object.values<any>(formValues)
      .flat()
      .map(({ changed, error, appliedPer, chargePeriod, chargeType, startDateTime, endDateTime, ...rest }) => ({
        ...rest,
        startDateTime: toTimezoneDate(toLocalDate(new Date(startDateTime)), timezone),
        endDateTime: endDateTime ? toTimezoneDate(toLocalDate(new Date(endDateTime)), timezone) : undefined,
      })) as any;

    const result = yield call(api.lots.updateLot, id, { id, additionalCosts }, FormName.lotEdit);

    yield put(
      updateLot.done({
        params: { id, formName: FormName.lotEdit },
        result,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('lots:cost_type_added'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateLotAdditionalCosts));
}

export function* deleteLotSaga(action: ReturnType<typeof deleteLot.started>) {
  try {
    const {
      meta,
      payload: { id, formName },
    } = action;
    const lots = yield select(getLotsFiltered);
    const isFirst = !!lots.find((lot, index) => lot.id === id && index === 0);
    yield call(api.lots.deleteLot, id, formName);

    yield put(setSelectedLotId(lots[isFirst ? 1 : 0].id));
    yield put(notificationSuccess('lots:delete_lot_success'));

    if (meta && meta.onSuccess) {
      meta.onSuccess();
    }

    yield put(getLots.started({}));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchLotsSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, getLotsSaga),
    yield takeLatest(getLots.started, getLotsSaga),
    yield takeLatest(getLotById.started, getLotByIdSaga),
    yield takeLatest(createLot.started, createLotSaga),
    yield takeLatest(updateLot.started, updateLotSaga),
    yield takeLatest(addTransfers.started, addTransfersSaga),
    yield takeLatest(addCloseouts.started, addCloseoutsSaga),
    yield takeLatest(addNote.started, addNoteSaga),
    yield takeLatest(deleteNote.started, deleteNoteSaga),
    yield takeLatest(editNote.started, editNoteSaga),
    yield takeLatest(updateLotMovements.started, updateLotMovementsSaga),
    yield takeLatest(editAdditionalCostsInLot.started, editAdditionalCostsInLotSaga),
    yield takeLatest(deleteLot.started, deleteLotSaga),
  ]);
}
